/**
 * CalendarTaskRegistry — H.3 concurrency cap for live slot indicators.
 *
 * Each calendar slot may have a `lastTaskId`. Naively subscribing every
 * slot to its own backend_tasks doc would explode listener count. The
 * registry caps active Firestore subscriptions at MAX_CONCURRENT (3 by
 * default) — extra slots fetch their last-known status from a server
 * snapshot instead of holding a live listener.
 *
 * The UI reads via `useCalendarSlotStatus(taskId)` which automatically
 * registers / unregisters and lets the provider rotate subscriptions as
 * components mount and unmount.
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { BackendTaskService } from '@modules/content-agent/services/BackendTaskService';

const MAX_CONCURRENT = 3;

export type SlotStatus = 'idle' | 'generating' | 'done' | 'error';

interface RegistryEntry {
  taskId: string;
  status: SlotStatus;
  unsubscribe?: () => void;
}

interface RegistryShape {
  projectId: string;
  byTaskId: Map<string, RegistryEntry>;
  /** taskIds in insertion order — first 3 hold live listeners. */
  order: string[];
  notifySubscribers: () => void;
  subscribers: Set<() => void>;
}

const CalendarTaskRegistryContext = createContext<RegistryShape | null>(null);

export function CalendarTaskRegistryProvider({
  projectId,
  children,
}: {
  projectId: string;
  children: React.ReactNode;
}) {
  const registryRef = useRef<RegistryShape | null>(null);
  if (!registryRef.current) {
    registryRef.current = {
      projectId,
      byTaskId: new Map(),
      order: [],
      subscribers: new Set(),
      notifySubscribers: () => {
        // Snapshot subscribers so re-renders during dispatch don't mutate the set.
        const snapshot = Array.from(registryRef.current?.subscribers ?? []);
        for (const s of snapshot) s();
      },
    };
  }
  return (
    <CalendarTaskRegistryContext.Provider value={registryRef.current}>
      {children}
    </CalendarTaskRegistryContext.Provider>
  );
}

function ensureSubscription(reg: RegistryShape, taskId: string) {
  const entry = reg.byTaskId.get(taskId);
  if (!entry) return;
  if (entry.unsubscribe) return;
  const taskService = new BackendTaskService();
  entry.unsubscribe = taskService.subscribe(
    reg.projectId,
    taskId,
    (task: any) => {
      const raw = task?.status as string | undefined;
      let next: SlotStatus = 'idle';
      if (raw === 'running') next = 'generating';
      else if (raw === 'completed') next = 'done';
      else if (raw === 'error' || raw === 'failed') next = 'error';
      entry.status = next;
      reg.notifySubscribers();
    },
    (_err) => {
      entry.status = 'error';
      reg.notifySubscribers();
    },
  );
}

function dropSubscription(reg: RegistryShape, taskId: string) {
  const entry = reg.byTaskId.get(taskId);
  if (entry?.unsubscribe) {
    entry.unsubscribe();
    entry.unsubscribe = undefined;
  }
}

function rotateSubscriptions(reg: RegistryShape) {
  const live = new Set<string>(reg.order.slice(0, MAX_CONCURRENT));
  for (const tid of reg.order) {
    if (live.has(tid)) ensureSubscription(reg, tid);
    else dropSubscription(reg, tid);
  }
}

/**
 * Subscribe a slot to its lastTaskId. Returns the latest status; under the
 * concurrency cap it's live, otherwise it's a stale read that may update
 * when other slots unmount.
 */
export function useCalendarSlotStatus(taskId: string | undefined | null): SlotStatus {
  const reg = useContext(CalendarTaskRegistryContext);
  const [, force] = useState({});

  useEffect(() => {
    if (!reg || !taskId) return;
    if (!reg.byTaskId.has(taskId)) {
      reg.byTaskId.set(taskId, { taskId, status: 'idle' });
    }
    if (!reg.order.includes(taskId)) reg.order.push(taskId);
    rotateSubscriptions(reg);
    const sub = () => force({});
    reg.subscribers.add(sub);
    return () => {
      reg.subscribers.delete(sub);
      const idx = reg.order.indexOf(taskId);
      if (idx >= 0) reg.order.splice(idx, 1);
      const otherUsers = Array.from(reg.subscribers).length;
      if (otherUsers === 0) {
        const e = reg.byTaskId.get(taskId);
        if (e?.unsubscribe) {
          e.unsubscribe();
          e.unsubscribe = undefined;
        }
        reg.byTaskId.delete(taskId);
      }
      rotateSubscriptions(reg);
    };
  }, [reg, taskId]);

  if (!reg || !taskId) return 'idle';
  return reg.byTaskId.get(taskId)?.status ?? 'idle';
}

/** Convenience selector — derive a coarse phase tag for badges. */
export function statusBadgeColor(status: SlotStatus): 'default' | 'info' | 'success' | 'error' {
  switch (status) {
    case 'generating':
      return 'info';
    case 'done':
      return 'success';
    case 'error':
      return 'error';
    default:
      return 'default';
  }
}

export const __TEST_ONLY__ = { MAX_CONCURRENT };
